import React, {Component} from 'react';
import {Button, Modal, ModalHeader, ModalFooter, ModalBody, FormGroup, Label, Input, FormFeedback} from 'reactstrap';
import PropTypes from "prop-types";

class LowInventoryAlertModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            enabled: false,
            alertType: 'INVENTORY_ONLY',
            threshold: 0,
            errors: {},
            isSubmitting: false,
            isLoading: true,
            saveSuccess: false
        };
    }

    componentDidMount() {
        if (this.props.productId) {
            this.loadAlertSettings();
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.productId && this.props.productId !== prevProps.productId) {
            this.loadAlertSettings();
        }
    }

    loadAlertSettings = () => {
        this.setState({ isLoading: true });

        fetch(`${process.env.REACT_APP_IM_PRODUCTS_URL || 'https://products.kjdelectronics.com'}/v1/products/${this.props.productId}/alerts`, {
                credentials: 'include'
            })
            .then(res => {
                if (!res.ok) throw new Error('Failed to load alert settings');
                return res.json();
            })
            .then(alerts => {
                if (alerts.lowInventory) {
                    this.setState({
                        enabled: alerts.lowInventory.enabled || false,
                        alertType: alerts.lowInventory.alertType || 'INVENTORY_ONLY',
                        threshold: alerts.lowInventory.threshold || 0,
                        isLoading: false
                    });
                } else {
                    this.setState({ isLoading: false });
                }
            })
            .catch(error => {
                console.error('Error loading alert settings:', error);
                this.setState({
                    isLoading: false,
                    errors: { load: 'Failed to load alert settings' }
                });
            });
    }

    validate = () => {
        const errors = {};
        const { threshold } = this.state;

        const parsedThreshold = parseInt(threshold);
        if (isNaN(parsedThreshold)) {
            errors.threshold = 'Threshold must be a number';
        } else if (parsedThreshold < 0) {
            errors.threshold = 'Threshold cannot be negative';
        }

        this.setState({ errors });
        return Object.keys(errors).length === 0;
    }

    handleSubmit = () => {
        if (!this.validate()) {
            return;
        }

        this.setState({ isSubmitting: true });

        const { enabled, alertType, threshold } = this.state;
        const body = {
            enabled,
            alertType,
            threshold: parseInt(threshold)
        };

        fetch(`${process.env.REACT_APP_IM_PRODUCTS_URL || 'https://products.kjdelectronics.com'}/v1/products/${this.props.productId}/alerts/low-inventory`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(body)
        })
        .then(res => {
            if (!res.ok) throw new Error('Failed to update alert settings');
            return res.json();
        })
        .then(() => {
            this.setState({ isSubmitting: false, errors: {}, saveSuccess: true });
            setTimeout(() => {
                this.props.onCancel();
            }, 1500);
        })
        .catch(error => {
            console.error('Error saving alert settings:', error);
            this.setState({
                isSubmitting: false,
                errors: { submit: 'Failed to save alert settings' }
            });
        });
    }

    render() {
        const { onCancel, productId } = this.props;
        const { enabled, alertType, threshold, errors, isSubmitting, isLoading, saveSuccess } = this.state;

        return (
            <div>
                <Modal isOpen={true} toggle={e => onCancel()} className={this.props.className} size="lg">
                    <ModalHeader toggle={e => onCancel()}>
                        Low Inventory Alert Settings - K{productId}
                    </ModalHeader>
                    <ModalBody>
                        {isLoading ? (
                            <div className="text-center">Loading...</div>
                        ) : (
                            <>
                                <FormGroup check className="mb-3">
                                    <Label check>
                                        <Input
                                            type="checkbox"
                                            checked={enabled}
                                            onChange={(e) => this.setState({ enabled: e.target.checked })}
                                        />
                                        {' '}Enable Low Inventory Alerts
                                    </Label>
                                </FormGroup>

                                <FormGroup tag="fieldset" disabled={!enabled}>
                                    <Label>Alert Type</Label>
                                    <FormGroup check>
                                        <Label check>
                                            <Input
                                                type="radio"
                                                name="alertType"
                                                value="INVENTORY_ONLY"
                                                checked={alertType === 'INVENTORY_ONLY'}
                                                onChange={(e) => this.setState({ alertType: e.target.value })}
                                                disabled={!enabled}
                                            />
                                            {' '}Inventory Only
                                        </Label>
                                    </FormGroup>
                                    <FormGroup check>
                                        <Label check>
                                            <Input
                                                type="radio"
                                                name="alertType"
                                                value="RESERVED_ONLY"
                                                checked={alertType === 'RESERVED_ONLY'}
                                                onChange={(e) => this.setState({ alertType: e.target.value })}
                                                disabled={!enabled}
                                            />
                                            {' '}Reserved Only
                                        </Label>
                                    </FormGroup>
                                    <FormGroup check>
                                        <Label check>
                                            <Input
                                                type="radio"
                                                name="alertType"
                                                value="COMBINED"
                                                checked={alertType === 'COMBINED'}
                                                onChange={(e) => this.setState({ alertType: e.target.value })}
                                                disabled={!enabled}
                                            />
                                            {' '}Combined Inventory and Reserve
                                        </Label>
                                    </FormGroup>
                                </FormGroup>

                                <FormGroup>
                                    <Label for="threshold">Alert Threshold</Label>
                                    <Input
                                        type="number"
                                        id="threshold"
                                        name="threshold"
                                        value={threshold}
                                        onChange={(e) => this.setState({ threshold: e.target.value })}
                                        invalid={!!errors.threshold}
                                        disabled={!enabled}
                                        min="0"
                                    />
                                    {errors.threshold && <FormFeedback>{errors.threshold}</FormFeedback>}
                                    <small className="form-text text-muted">
                                        Alert will trigger when inventory drops below this value (0 is valid)
                                    </small>
                                </FormGroup>

                                {saveSuccess && (
                                    <div className="alert alert-success mt-3">Alert settings saved successfully!</div>
                                )}
                                {errors.submit && (
                                    <div className="alert alert-danger mt-3">{errors.submit}</div>
                                )}
                                {errors.load && (
                                    <div className="alert alert-warning mt-3">{errors.load}</div>
                                )}
                            </>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={e => onCancel()} disabled={isSubmitting}>
                            Close
                        </Button>
                        <Button color="primary" onClick={this.handleSubmit} disabled={isSubmitting || isLoading}>
                            {isSubmitting ? 'Saving...' : 'Save'}
                        </Button>
                    </ModalFooter>
                </Modal>
            </div>
        );
    }
}

LowInventoryAlertModal.propTypes = {
    onCancel: PropTypes.func.isRequired,
    productId: PropTypes.number.isRequired
};

export {LowInventoryAlertModal};
